import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { ROUTES } from "@/constants/routes";
import { useIntegrations } from "@/features/integrations/hooks/useIntegrations";
import { decodeSlug } from "@/features/integrations/utils/slug";
import { useAuth } from "@/lib/hooks/useAuth";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";

export function useCampaignsClientDetailsBreadcrumbs() {
  const { t, i18n } = useTranslation();
  const { clientId, integrationName } = useParams<{
    clientId?: string;
    integrationName?: string;
  }>();
  const { tenantId } = useAuth();
  const integrationsQuery = useIntegrations({ tenantId, clientId });

  const decodedIntegrationName = integrationName
    ? decodeSlug(integrationName)
    : null;
  const integration =
    integrationsQuery.data?.find(
      (i) => i.name === decodedIntegrationName
    ) ?? null;

  const integrationDisplayName =
    integration?.displayName && i18n.exists(integration.displayName)
      ? t(integration.displayName)
      : integration?.displayName;

  const moduleDisplayName = t("integrations.modules.names.campaigns");

  const breadcrumbs = useBreadcrumbs({
    items: clientId
      ? [
        {
          label: t("common.integrations"),
          href: `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}`,
        },
        {
          label: integrationDisplayName ?? t("common.integration"),
          href:
            clientId && integrationName
              ? `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}/${integrationName}`
              : undefined,
        },
        {
          label: moduleDisplayName,
        },
      ]
      : [
        { label: t("common.integrations"), href: `/${ROUTES.integrations}` },
        {
          label: integrationDisplayName ?? t("common.integration"),
          href: integrationName
            ? `/${ROUTES.integrations}/${integrationName}`
            : undefined,
        },
        {
          label: moduleDisplayName,
        },
      ],
  });

  return { breadcrumbs, integration };
}
